import { useCallback, useMemo } from 'react';
import {
  useQueries,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  WorkbookDefinition,
  WorkbookRun,
  WorkbookRunDetails,
} from '@erp/shared-types';
import { sheetFingerprints } from '@erp/workbooks';
import { orpc } from '@/api/rpc/client';
import { workbookRunResults } from './workbookRunResults';

/** While a Run is queued or running: its Results show within a second of completing. */
const ACTIVE_POLL_MS = 1000;
/**
 * Otherwise a slow check, for Runs started elsewhere (a Workflow, an agent,
 * another tab); there is no push channel for Run events to wait on instead.
 */
const IDLE_POLL_MS = 30_000;

/** Whether a Run is queued or running. */
export function hasActiveRun(
  runs: readonly WorkbookRun[] | undefined,
): boolean {
  return (
    runs?.some((run) => run.status === 'queued' || run.status === 'running') ??
    false
  );
}

/** How often the Run list refreshes: quickly only while a Run is active. */
export function runsPollInterval(
  runs: readonly WorkbookRun[] | undefined,
): number {
  return hasActiveRun(runs) ? ACTIVE_POLL_MS : IDLE_POLL_MS;
}

/** The Runs whose Results the Worksheets show, whose details they read. */
export function detailedRunIds(
  runs: readonly WorkbookRun[] | undefined,
  fingerprints: ReadonlyMap<string, string>,
): string[] {
  const results = workbookRunResults(runs, fingerprints);
  const ids = new Set<string>();
  for (const sheetId of fingerprints.keys()) {
    const { run } = results.sheet(sheetId);
    if (run) ids.add(run.id);
  }
  return [...ids];
}

/** The listed Runs with the details read so far put back on their Results. */
export function withRunDetails(
  runs: WorkbookRun[],
  details: ReadonlyMap<string, WorkbookRunDetails>,
): WorkbookRun[] {
  if (!details.size) return runs;
  return runs.map((run) => {
    const read = details.get(run.id);
    if (!read) return run;
    return {
      ...run,
      results: run.results.map((result) => {
        const detail = read.find((item) => item.sheetId === result.sheetId);
        return detail ? { ...result, ...detail } : result;
      }),
    };
  });
}

/**
 * The Workbook's Runs, polled quickly only while one is active, with the
 * details (Distinct values, Input-order preview) of the Runs its Worksheets
 * show read once per Run — the Run list leaves them out.
 */
function useWorkbookRuns(workbookId: string, definition: WorkbookDefinition) {
  const listed = useQuery(
    orpc.workbooks.runs.queryOptions({
      input: { workbookId },
      refetchInterval: (query) => runsPollInterval(query.state.data),
    }),
  );
  const fingerprints = useMemo(
    () => sheetFingerprints(definition),
    [definition],
  );

  // Keyed by content: an edit that shows the same Runs keeps the same list.
  const idsKey = detailedRunIds(listed.data, fingerprints).join(',');
  const ids = useMemo(() => (idsKey ? idsKey.split(',') : []), [idsKey]);
  // Stable while the Runs shown are, so the details keep their identity
  // between renders and what derives from the Runs isn't recomputed.
  const combine = useCallback(
    (queries: UseQueryResult<WorkbookRunDetails>[]) =>
      new Map(
        queries.flatMap((query, index) => {
          const runId = ids[index];
          return query.data && runId ? [[runId, query.data] as const] : [];
        }),
      ),
    [ids],
  );
  const details = useQueries({
    queries: ids.map((runId) =>
      orpc.workbooks.runDetails.queryOptions({
        input: { workbookId, runId },
        // A Run's Results never change.
        staleTime: Infinity,
      }),
    ),
    combine,
  });
  const data = useMemo(
    () => listed.data && withRunDetails(listed.data, details),
    [listed.data, details],
  );
  const { error, isPending, refetch } = listed;
  // Only what the editor reads, so a poll's fetching status re-renders nothing.
  return useMemo(
    () => ({ data, error, isPending, refetch }),
    [data, error, isPending, refetch],
  );
}

export function useWorkbookQueries(
  workbookId: string,
  definition: WorkbookDefinition,
) {
  const runs = useWorkbookRuns(workbookId, definition);
  const versions = useQuery(
    orpc.workbooks.versions.queryOptions({ input: { workbookId } }),
  );
  const catalog = useQuery(orpc.workbooks.catalog.queryOptions({ input: {} }));

  return { runs, versions, catalog };
}
